import { useEffect, useState } from 'react';
import { Copy, Check, X, RefreshCw } from 'lucide-react';
import api from '../services/api';

interface ContactSummary {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  title: string | null;
}

interface DuplicatePair {
  contact_a: ContactSummary;
  contact_b: ContactSummary;
  match_type: string;
  confidence: number;
}

export default function DedupPage() {
  const [pairs, setPairs] = useState<DuplicatePair[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dedup/candidates');
      setPairs(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const merge = async (primaryId: string, duplicateId: string) => {
    setActionLoading(`merge-${primaryId}-${duplicateId}`);
    try {
      await api.post('/dedup/merge', { primary_id: primaryId, duplicate_id: duplicateId });
      setPairs((prev) =>
        prev.filter(
          (p) =>
            !(
              (p.contact_a.id === primaryId && p.contact_b.id === duplicateId) ||
              (p.contact_a.id === duplicateId && p.contact_b.id === primaryId)
            ),
        ),
      );
    } catch {
      /* ignore */
    } finally {
      setActionLoading(null);
    }
  };

  const dismiss = async (aId: string, bId: string) => {
    setActionLoading(`dismiss-${aId}-${bId}`);
    try {
      await api.post('/dedup/dismiss', { contact_a_id: aId, contact_b_id: bId });
      setPairs((prev) =>
        prev.filter(
          (p) =>
            !(
              (p.contact_a.id === aId && p.contact_b.id === bId) ||
              (p.contact_a.id === bId && p.contact_b.id === aId)
            ),
        ),
      );
    } catch {
      /* ignore */
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const confidenceColor = (c: number) => {
    if (c >= 90) return '#ef4444';
    if (c >= 70) return '#f59e0b';
    return '#6b7280';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Copy size={24} style={{ color: 'var(--netrun-green)' }} />
            Contact Dedup
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Find and merge duplicate contacts
          </p>
        </div>
        <button
          onClick={fetchDuplicates}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
          style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>Scanning for duplicates...</div>
      ) : pairs.length === 0 ? (
        <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>
          <Check size={48} className="mx-auto mb-3 opacity-30" />
          <p>No duplicates found. Your contacts are clean.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {pairs.length} potential duplicate pair{pairs.length !== 1 ? 's' : ''} found
          </p>

          {pairs.map((pair, idx) => {
            const a = pair.contact_a;
            const b = pair.contact_b;
            const mergeKeyA = `merge-${a.id}-${b.id}`;
            const mergeKeyB = `merge-${b.id}-${a.id}`;
            const dismissKey = `dismiss-${a.id}-${b.id}`;

            return (
              <div
                key={idx}
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                    <span
                      className="px-2 py-0.5 rounded-full font-medium"
                      style={{ background: `${confidenceColor(pair.confidence)}20`, color: confidenceColor(pair.confidence) }}
                    >
                      {pair.confidence}% match
                    </span>
                    <span>via {pair.match_type === 'email' ? 'exact email' : 'fuzzy name'}</span>
                  </div>
                  <button
                    onClick={() => dismiss(a.id, b.id)}
                    disabled={actionLoading === dismissKey}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-white/5"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    <X size={12} /> Not a duplicate
                  </button>
                </div>

                {/* Side-by-side comparison */}
                <div className="grid grid-cols-2 divide-x" style={{ borderColor: 'var(--border-subtle)' }}>
                  {[a, b].map((contact, ci) => {
                    const otherId = ci === 0 ? b.id : a.id;
                    const mergeKey = ci === 0 ? mergeKeyA : mergeKeyB;
                    return (
                      <div key={contact.id} className="p-4 space-y-2">
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {contact.first_name} {contact.last_name}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {contact.email || 'No email'}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {contact.title || 'No title'}
                        </div>
                        <button
                          onClick={() => merge(contact.id, otherId)}
                          disabled={actionLoading === mergeKey}
                          className="mt-2 w-full flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                          style={{
                            background: actionLoading === mergeKey ? 'var(--bg-tertiary)' : 'var(--netrun-green)',
                            color: actionLoading === mergeKey ? 'var(--text-muted)' : '#000',
                          }}
                        >
                          <Check size={12} />
                          Keep this one
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
